import { useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconBuildingFactory2,
  IconCircleCheck,
  IconCircleX,
  IconDiscount2,
  IconFlag
} from '@tabler/icons-react'
import Link from 'next/link'

import { DeliveryMethod } from 'modules/delivery'
import ExpandablePanel from 'shared/ui/ExpandablePanel'
import { theme } from 'shared/ui/utils/designTokens'
import { formatDate } from 'shared/utils/date'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { PageProductTypes } from '../../../types/products'
import { boldAllergensInString } from '../../../utils/allergens'
import {
  getProductPackDetails,
  getProductUnitDetails
} from '../../../utils/products'

import styles from './ProductDetailsAccordion.module.scss'

interface ProductDetailsAccordionProps {
  product: PageProductTypes<DeliveryMethod>
}

const ProductDetailsAccordion = ({ product }: ProductDetailsAccordionProps) => {
  const isAvailable = product.isAvailable
  const hasDiscount = isNotNilOrEmpty(product.specialOfferPricing)
  const hasSpecialOfferEndDate = isNotNilOrEmpty(product.specialOfferEndsAt)
  const hasAgeLimit = isNotNilOrEmpty(product?.ageLimit)
  // const hasShelfLifeTime = isNotNilOrEmpty(product?.shelfLifeMinForSale)
  const [allergens, setAllergens] = useState<string>('')
  const [ingredients, setIngredients] = useState<string>('')

  useEffect(() => {
    const tempElement = document?.createElement('div')

    if (product?.allergens) {
      tempElement.innerHTML = product.allergens
      setAllergens(boldAllergensInString(tempElement.innerHTML))
    }

    if (product?.ingredients) {
      tempElement.innerHTML = product.ingredients
      setIngredients(boldAllergensInString(tempElement.innerHTML))
    }

    tempElement.remove()
  }, [product])

  const setItems = product?.setItems || []

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        title='Informacje główne'
        id={`product-details-accordion-main-info-${product.id}`}
      >
        {isAvailable ? (
          <div className={styles.detailIconRow}>
            <IconCircleCheck color={theme.colors.brand.secondary} />
            <p>Produkt dostępny</p>
          </div>
        ) : (
          <div className={styles.detailIconRow}>
            <IconCircleX color={theme.colors.system.error.text} />
            <p>Produkt niedostępny</p>
          </div>
        )}
        {isNotNilOrEmpty(product?.producer?.name) && (
          <div className={styles.detailIconRow}>
            <IconBuildingFactory2 color={theme.colors.brand.secondary} />
            <p>{product?.producer?.name}</p>
          </div>
        )}
        {hasDiscount && (
          <div className={styles.detailIconRow}>
            <IconDiscount2 color={theme.colors.brand.secondary} />
            <p>
              Produkt w promocji{' '}
              {hasSpecialOfferEndDate
                ? `do ${formatDate(product.specialOfferEndsAt)}`
                : ''}
            </p>
          </div>
        )}
        {hasAgeLimit && (
          <div className={styles.detailIconRow}>
            <IconAlertTriangle color={theme.colors.brand.secondary} />
            <p>{`Produkt od ${String(product.ageLimit)} lat`}</p>
          </div>
        )}
        {product.brand?.slug && (
          <div className={styles.detailIconRow}>
            <IconCircleCheck color={theme.colors.brand.secondary} />
            <p>
              Zobacz produkty od wytwórcy{' '}
              <Link
                className={styles.brandLink}
                href={`${PATHS.brand}/${product.brand.slug as string}`}
                {...addTestId('product-page-brand-link')}
              >
                {product.brand.name} {'>'}
              </Link>
            </p>
          </div>
        )}
      </ExpandablePanel>
      {isNotNilOrEmpty(product.countriesOfOrigin) && (
        <ExpandablePanel
          title='Szczegóły produktu'
          id={`product-details-accordion-product-details-${product.id}`}
        >
          <div className={styles.detailIconRow}>
            <IconFlag color={theme.colors.brand.secondary} />
            <p>Kraj pochodzenia: {product.countriesOfOrigin.join('/')}</p>
          </div>
        </ExpandablePanel>
      )}
      {isNotNilOrEmpty(product.nutritionalValue) && (
        <ExpandablePanel
          title='Wartości odżywcze'
          id={`product-details-accordion-nutritional-value-${product.id}`}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: product?.nutritionalValue || ''
            }}
          />
        </ExpandablePanel>
      )}
      {(isNotNilOrEmpty(product?.ingredients) ||
        isNotNilOrEmpty(product?.allergens)) && (
        <ExpandablePanel
          title='Składniki i alergeny'
          id='product-details-accordion-ingredients-allergens'
        >
          <div>
            {isNotNilOrEmpty(product?.ingredients) && (
              <div className={styles.insideSection}>
                <h3 className={styles.insideSectionTitle}>Skład:</h3>
                <div dangerouslySetInnerHTML={{ __html: ingredients }} />
              </div>
            )}
            {isNotNilOrEmpty(product?.allergens) && (
              <div className={styles.insideSection}>
                <h3 className={styles.insideSectionTitle}>Alergeny:</h3>
                <div dangerouslySetInnerHTML={{ __html: allergens }} />
              </div>
            )}
          </div>
        </ExpandablePanel>
      )}
      {isNotNilOrEmpty(product?.storageConditions) && (
        <ExpandablePanel
          title='Przechowywanie'
          id={`product-details-accordion-storage-conditions-${product.id}`}
        >
          <div className={styles.insideSection}>
            <h3 className={styles.insideSectionTitle}>Warunki przechowywania:</h3>
            <div
              dangerouslySetInnerHTML={{
                __html: product?.storageConditions || '---'
              }}
            />
          </div>
        </ExpandablePanel>
      )}
      <ExpandablePanel
        title='Opakowanie'
        expanded={isNotNilOrEmpty(setItems)}
        id={`product-details-accordion-packaging-${product.id}`}
      >
        {isNotNilOrEmpty(setItems) && (
          <div className={styles.setItemsWrapper}>
            <div className={styles.setItemsLabel}>W skład zestawu wchodzą:</div>
            {setItems?.map(item => (
              <div className={styles.singleSetItem} key={item.product.nameLong}>
                <Link className={styles.productLink} href={item.product.slug}>
                  {item.product.nameLong} {getProductUnitDetails(item.product)}
                </Link>
                <span>{item.quantity}</span>
              </div>
            ))}
          </div>
        )}
        <div
          dangerouslySetInnerHTML={{ __html: getProductPackDetails(product) }}
        />
      </ExpandablePanel>
      {isNotNilOrEmpty(product.warnings) && (
        <ExpandablePanel
          title='Ostrzeżenia'
          id={`product-details-accordion-warnings-${product.id}`}
        >
          <div dangerouslySetInnerHTML={{ __html: product?.warnings || '' }} />
        </ExpandablePanel>
      )}
    </div>
  )
}

export default ProductDetailsAccordion
